import React, { useState } from 'react';
import ChatbotFixed from './ChatbotFixed';

const SimpleTester = () => {
  const [context, setContext] = useState('');
  const [generatedQuery, setGeneratedQuery] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [isAutoTesting, setIsAutoTesting] = useState(false);
  const [conversationHistory, setConversationHistory] = useState([]);
  const [logs, setLogs] = useState([]);
  const [queryCount, setQueryCount] = useState(0);

  const addLog = (message, type = 'info') => {
    const timestamp = new Date().toLocaleTimeString();
    setLogs(prev => [...prev.slice(-5), { timestamp, message, type }]);
  };

  const generateQuery = async (useHistory = false) => {
    if (!context.trim()) {
      alert('Please enter context first');
      return null;
    }

    setIsGenerating(true);
    addLog('🧠 Calling LLM to generate query...', 'info');

    try {
      let framework;
      
      if (useHistory && conversationHistory.length > 0) {
        // Include conversation history for contextual generation
        const recentHistory = conversationHistory.slice(-2); // Last 2 exchanges
        const historyText = recentHistory.map(h => `Customer asked: "${h.question}" | Jazeera Airways replied: "${h.response}"`).join(' | ');
        framework = `${context}. Previous conversation: ${historyText}`;
        addLog(`📝 Using conversation history: ${recentHistory.length} exchanges`, 'info');
      } else {
        framework = `You are testing Jazeera Airways chatbot. ${context}`;
        addLog(`📝 First query - no history used`, 'info');
      }

      addLog(`📡 Sending to LLM: "${framework.substring(0, 100)}..."`, 'info');
      
      const response = await fetch('http://localhost:7071/api/generate-query', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          framework: framework
        })
      });

      addLog(`📡 API Response: ${response.status}`, response.ok ? 'success' : 'error');

      if (response.ok) {
        const data = await response.json();
        const query = data.query?.trim();
        
        if (query) {
          setGeneratedQuery(query);
          addLog(`✅ Generated: "${query}"`, 'success');
          return query;
        } else {
          addLog('❌ No query returned', 'error');
        }
      } else {
        addLog(`❌ API failed: ${response.status}`, 'error');
      }
    } catch (error) {
      addLog(`❌ Error: ${error.message}`, 'error');
    }

    setIsGenerating(false);
    return null;
  };

  const sendToChatbot = async (query) => {
    if (!query) {
      addLog('❌ No query to send', 'error');
      return false;
    }

    addLog('🔍 Looking for chatbot input...', 'info');

    // Enhanced input field detection
    const inputSelectors = [
      'input[placeholder*="Enter next query"]',
      'input[placeholder*="أدخل استفسارك التالي"]',
      'input[type="text"]:not([disabled])',
      '.chatbot-fixed-container input[type="text"]',
      'input'
    ];

    let input = null;
    for (const selector of inputSelectors) {
      const inputs = document.querySelectorAll(selector);
      for (const inp of inputs) {
        if (inp && inp.offsetParent !== null && !inp.disabled) {
          input = inp;
          break;
        }
      }
      if (input) break;
    }

    if (!input) {
      addLog('❌ No input field found', 'error');
      return false;
    }

    addLog('✅ Found input field', 'success');

    // Type with React-compatible events
    input.focus();
    const nativeInputValueSetter = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, 'value').set;
    nativeInputValueSetter.call(input, query);
    input.dispatchEvent(new Event('input', { bubbles: true }));
    input.dispatchEvent(new Event('change', { bubbles: true }));

    addLog('⌨️ Typed query into input', 'success');

    // Find and click send button
    return new Promise((resolve) => {
      setTimeout(() => {
        const buttonSelectors = [
          'button[title*="Send"]',
          'button[style*="borderRadius: 50%"]',
          'button:has(svg)',
          'button[type="submit"]',
          'button:not([disabled])'
        ];

        let sendBtn = null;
        for (const selector of buttonSelectors) {
          try {
            const buttons = document.querySelectorAll(selector);
            for (const btn of buttons) {
              if (btn && btn.offsetParent !== null && !btn.disabled) {
                sendBtn = btn;
                break;
              }
            }
            if (sendBtn) break;
          } catch (e) {
            continue;
          }
        }

        if (sendBtn) {
          addLog('✅ Found send button, clicking...', 'success');
          sendBtn.click();
          addLog(`📤 Sent: "${query}"`, 'success');
          resolve(true);
        } else {
          addLog('⚠️ No send button found, trying Enter key', 'warning');
          input.dispatchEvent(new KeyboardEvent('keydown', { 
            key: 'Enter', 
            code: 'Enter',
            bubbles: true 
          }));
          addLog(`📤 Sent via Enter: "${query}"`, 'success');
          resolve(true);
        }
      }, 1000);
    });
  };

  const captureResponse = async (question) => {
    addLog('⏳ Waiting 5 seconds for response...', 'info');
    
    // Simple approach: wait 5 seconds then grab whatever text is there
    await new Promise(resolve => setTimeout(resolve, 5000));
    
    const chatContainer = document.querySelector('.chatbot-fixed-container, [class*="chatbot"], [class*="chat"]');
    if (chatContainer) {
      const allText = chatContainer.textContent || '';
      
      // Look for any response that's not the question
      const lines = allText.split('\n').filter(line => 
        line.trim() && 
        line.trim() !== question &&
        line.length > 10 &&
        !line.includes('Magic') &&
        !line.includes('Edit')
      );
      
      // Get the last meaningful line as response
      const response = lines[lines.length - 1] || 'I can help you with flight booking';
      
      addLog(`✅ Captured: "${response.substring(0, 50)}..."`, 'success');
      
      // Add to conversation history
      setConversationHistory(prev => {
        const exists = prev.some(item => item.question === question);
        if (!exists) {
          return [...prev, { question, response }];
        }
        return prev;
      });
      
      return response;
    }
    
    // Fallback
    const fallbackResponse = 'I can help you with Jazeera Airways flight booking';
    setConversationHistory(prev => {
      const exists = prev.some(item => item.question === question);
      if (!exists) {
        return [...prev, { question, response: fallbackResponse }];
      }
      return prev;
    });
    
    return fallbackResponse;
  };

  const runAutomaticTest = async () => {
    if (!context.trim()) {
      alert('Please enter context first');
      return;
    }

    const currentCount = queryCount + 1;
    addLog(`🤖 Starting Jazeera test #${currentCount}`, 'info');
    
    // Generate query (use history if not first query)
    const query = await generateQuery(conversationHistory.length > 0);
    if (!query) {
      addLog('❌ Failed to generate query', 'error');
      // Continue anyway
      if (isAutoTesting) {
        setTimeout(() => runAutomaticTest(), 5000);
      }
      return;
    }

    // Send to chatbot
    const sent = await sendToChatbot(query);
    if (!sent) {
      addLog('❌ Failed to send query', 'error');
      // Continue anyway
      if (isAutoTesting) {
        setTimeout(() => runAutomaticTest(), 5000);
      }
      return;
    }

    // Capture response
    await captureResponse(query);
    
    setQueryCount(prev => prev + 1);
    addLog(`✅ Jazeera test #${currentCount} completed`, 'success');
    
    // Schedule next test
    if (isAutoTesting) {
      addLog('⏰ Next test in 8 seconds...', 'info');
      setTimeout(() => {
        if (isAutoTesting) {
          runAutomaticTest();
        }
      }, 8000);
    }
  };

  const startAutoTesting = () => {
    setIsAutoTesting(true);
    setQueryCount(0);
    setConversationHistory([]);
    addLog('🚀 Starting automatic testing...', 'success');
    runAutomaticTest();
  };

  const stopAutoTesting = () => {
    setIsAutoTesting(false);
    addLog('⏹️ Stopped automatic testing', 'info');
  };

  return (
    <div style={{ 
      minHeight: '100vh',
      backgroundColor: '#f5f7fa',
      padding: '20px',
      fontFamily: 'Arial, sans-serif'
    }}>
      <div style={{
        maxWidth: '1400px',
        margin: '0 auto',
        backgroundColor: '#fff',
        borderRadius: '12px',
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
        overflow: 'hidden'
      }}>
        {/* Header */}
        <div style={{
          backgroundColor: '#e53e3e',
          padding: '20px 30px',
          color: 'white'
        }}>
          <h1 style={{ margin: 0, fontSize: '24px', fontWeight: 'bold' }}>🤖 Simple LLM Tester</h1>
          <p style={{ margin: '5px 0 0', opacity: 0.8 }}>Context → LLM → Chatbot</p>
        </div>

        {/* Main Content */}
        <div style={{ display: 'flex', height: '700px' }}>
          
          {/* Left Side - Context */}
          <div style={{ 
            width: '40%', 
            padding: '20px', 
            borderRight: '2px solid #eee',
            backgroundColor: '#f8f9fa'
          }}>
            <h3 style={{ margin: '0 0 15px', color: '#e53e3e' }}>📝 Context Input</h3>
            
            <textarea
              value={context}
              onChange={(e) => setContext(e.target.value)}
              placeholder="Enter context for testing Jazeera Airways chatbot...

Example: You are a customer testing Jazeera Airways flight booking system. Generate natural questions about flights, schedules, prices, baggage, meals, and booking process. Ask follow-up questions based on chatbot responses."
              style={{
                width: '100%',
                height: '200px',
                padding: '15px',
                border: '1px solid #ddd',
                borderRadius: '8px',
                fontSize: '14px',
                resize: 'vertical',
                marginBottom: '15px'
              }}
            />

            {!isAutoTesting ? (
              <button
                onClick={startAutoTesting}
                disabled={isGenerating}
                style={{
                  backgroundColor: '#007bff',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '15px 24px',
                  cursor: isGenerating ? 'not-allowed' : 'pointer',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  width: '100%',
                  marginBottom: '15px'
                }}
              >
                🚀 Submit Context & Start Auto Testing
              </button>
            ) : (
              <button
                onClick={stopAutoTesting}
                style={{
                  backgroundColor: '#dc3545',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '15px 24px',
                  cursor: 'pointer',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  width: '100%',
                  marginBottom: '15px'
                }}
              >
                ⏹️ Stop Automatic Testing
              </button>
            )}

            {/* Current Query Display */}
            {generatedQuery && (
              <div style={{
                backgroundColor: '#e3f2fd',
                border: '1px solid #bbdefb',
                borderRadius: '8px',
                padding: '15px',
                marginBottom: '15px'
              }}>
                <h4 style={{ margin: '0 0 10px', color: '#1565c0' }}>🤖 Current Query:</h4>
                <p style={{ margin: '0', fontSize: '14px', fontStyle: 'italic' }}>
                  "{generatedQuery}"
                </p>
              </div>
            )}

            {/* Status */}
            <div style={{ marginBottom: '15px', fontSize: '14px' }}>
              <div>Tests completed: <strong>{queryCount}</strong></div>
              <div>Conversation pairs: <strong>{conversationHistory.length}</strong></div>
              {isAutoTesting && <div style={{ color: '#007bff' }}>● Auto Testing Active</div>}
              <button
                onClick={() => {
                  console.log('Conversation History:', conversationHistory);
                  alert(`History: ${conversationHistory.length} pairs. Check console for details.`);
                }}
                style={{
                  backgroundColor: '#ffc107',
                  color: '#212529',
                  border: 'none',
                  borderRadius: '4px',
                  padding: '4px 8px',
                  fontSize: '11px',
                  cursor: 'pointer',
                  marginTop: '5px'
                }}
              >
                🔍 Debug History
              </button>
            </div>

            {/* Conversation History */}
            <div style={{ marginBottom: '15px' }}>
              <h4 style={{ margin: '0 0 10px', color: '#666' }}>💬 Conversation</h4>
              <div style={{
                backgroundColor: '#fff',
                border: '1px solid #ddd',
                borderRadius: '8px',
                padding: '10px',
                height: '120px',
                overflowY: 'auto',
                fontSize: '12px'
              }}>
                {conversationHistory.length === 0 ? (
                  <div style={{ color: '#999', fontStyle: 'italic' }}>Conversation history will appear here...</div>
                ) : (
                  conversationHistory.map((item, index) => (
                    <div key={index} style={{ marginBottom: '8px', borderBottom: '1px solid #eee', paddingBottom: '5px' }}>
                      <div style={{ color: '#007bff', fontWeight: 'bold' }}>Q{index + 1}: {item.question}</div>
                      <div style={{ color: '#28a745', marginTop: '2px' }}>A: {item.response.substring(0, 100)}...</div>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Logs */}
            <div>
              <h4 style={{ margin: '0 0 10px', color: '#666' }}>📋 Logs</h4>
              <div style={{
                backgroundColor: '#fff',
                border: '1px solid #ddd',
                borderRadius: '8px',
                padding: '10px',
                height: '100px',
                overflowY: 'auto',
                fontSize: '12px'
              }}>
                {logs.length === 0 ? (
                  <div style={{ color: '#999', fontStyle: 'italic' }}>Logs will appear here...</div>
                ) : (
                  logs.slice(-10).map((log, index) => (
                    <div key={index} style={{ 
                      marginBottom: '3px',
                      color: log.type === 'success' ? '#28a745' : log.type === 'error' ? '#dc3545' : '#6c757d'
                    }}>
                      [{log.timestamp}] {log.message}
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
          
          {/* Right Side - Chatbot */}
          <div style={{ width: '60%', padding: '20px' }}>
            <h3 style={{ margin: '0 0 15px', color: '#e53e3e' }}>💬 Chatbot</h3>
            
            <div style={{
              height: 'calc(100% - 50px)',
              border: '2px solid #e53e3e',
              borderRadius: '8px',
              overflow: 'hidden'
            }}>
              <ChatbotFixed />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SimpleTester;